import { useSortable } from '@dnd-kit/sortable';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

import { DragAllDirectionIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../../../../buttons/button/Button.js';
import { useModality } from '../../../../../../core/hooks/useModality.js';
import { focusRing } from '../../../../../../core/styles/focusRing.sty.js';
import { dataTableMessages } from '../../../../messages.js';
import * as styles from './ColumnOrderSettingsDragAndDrop.sty.js';

/**
 * A drag handle for columns in the column settings.
 * @internal
 */
export const ColumnOrderDragHandle = ({
  columnKey,
  columnId,
  isDraggingActive,
}: {
  /**
   * The unique key of the column.
   */
  columnKey: string;
  /**
   * The id of the column.
   */
  columnId: string;
  /**
   * Whether dragging is currently active.
   */
  isDraggingActive: boolean;
}) => {
  const intl = useIntl();
  const modality = useModality();
  const { attributes, listeners } = useSortable({ id: columnKey });

  const isKeyboardDragging = isDraggingActive && modality === 'keyboard';
  const localColumnDragHandleLabel = intl.formatMessage(
    dataTableMessages.columnSettingsDragAndDropLabel,
    { columnId: columnId },
  );

  return (
    <Button
      size="condensed"
      variant="default"
      {...attributes}
      {...listeners}
      aria-label={localColumnDragHandleLabel}
      className={clsx(
        styles.columnDndHandle,
        isKeyboardDragging && focusRing({ minimal: false, color: 'neutral' }),
      )}
      data-dt-children-name="ColumnSettingsDragAndDrop"
    >
      <Button.Prefix>
        <DragAllDirectionIcon />
      </Button.Prefix>
    </Button>
  );
};
